/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { FolderTree, Layers, GitMerge } from 'lucide-react';
import type { GitLabProject } from '../../services/gitlabService';

interface GitLabRepoSelectorProps {
  projects: GitLabProject[];
  selectedRepoUrl: string;
  onSelectedRepoUrlChange: (url: string) => void;
  disabled?: boolean;
}

/**
 * Three-level hierarchical GitLab repository selector.
 *
 * Breaks GitLab's `path_with_namespace` (e.g. "acme/backend/api-service")
 * into Group → Namespace → Repository for easier browsing.
 */
export function GitLabRepoSelector({
  projects,
  selectedRepoUrl,
  onSelectedRepoUrlChange,
  disabled,
}: GitLabRepoSelectorProps) {
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedNamespace, setSelectedNamespace] = useState('');

  const groups = useMemo(() => {
    const groupMap = new Map<string, number>();
    for (const project of projects) {
      const parts = project.path_with_namespace.split('/');
      if (parts.length >= 2) {
        const group = parts[0];
        groupMap.set(group, (groupMap.get(group) || 0) + 1);
      }
    }
    return Array.from(groupMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, count]) => ({ name, count }));
  }, [projects]);

  const namespaces = useMemo(() => {
    if (!selectedGroup) return [];
    const namespaceMap = new Map<string, number>();
    for (const project of projects) {
      const parts = project.path_with_namespace.split('/');
      if (parts[0] === selectedGroup) {
        const namespace = parts.slice(0, -1).join('/');
        namespaceMap.set(namespace, (namespaceMap.get(namespace) || 0) + 1);
      }
    }
    return Array.from(namespaceMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([fullPath, count]) => ({
        fullPath,
        displayName: fullPath === selectedGroup
          ? '(direct)'
          : fullPath.slice(selectedGroup.length + 1),
        count,
      }));
  }, [projects, selectedGroup]);

  const filteredProjects = useMemo(() => {
    if (!selectedNamespace) return [];
    return projects
      .filter(p => {
        const parts = p.path_with_namespace.split('/');
        const namespace = parts.slice(0, -1).join('/');
        return namespace === selectedNamespace;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projects, selectedNamespace]);

  useEffect(() => {
    if (!selectedRepoUrl) {
      setSelectedGroup('');
      setSelectedNamespace('');
    }
  }, [selectedRepoUrl]);

  const handleGroupChange = (group: string) => {
    setSelectedGroup(group);
    setSelectedNamespace('');
    onSelectedRepoUrlChange('');
  };

  const handleNamespaceChange = (namespace: string) => {
    setSelectedNamespace(namespace);
    onSelectedRepoUrlChange('');
  };

  return (
    <div className="space-y-3">
      {/* Group Selector */}
      <div className="space-y-2">
        <label className="text-sm font-medium flex items-center gap-2">
          <FolderTree className="h-4 w-4" />
          Group
        </label>
        <select
          value={selectedGroup}
          onChange={(e) => handleGroupChange(e.target.value)}
          className="w-full p-2 border rounded-md bg-background"
          disabled={disabled}
        >
          <option value="">Choose a group...</option>
          {groups.map(({ name, count }) => (
            <option key={name} value={name}>
              {name} ({count} project{count !== 1 ? 's' : ''})
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">
          {groups.length} group{groups.length !== 1 ? 's' : ''} with{' '}
          {projects.length} total project{projects.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Namespace Selector */}
      {selectedGroup && (
        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Namespace
          </label>
          <select
            value={selectedNamespace}
            onChange={(e) => handleNamespaceChange(e.target.value)}
            className="w-full p-2 border rounded-md bg-background"
            disabled={disabled}
          >
            <option value="">Choose a namespace...</option>
            {namespaces.map(({ fullPath, displayName, count }) => (
              <option key={fullPath} value={fullPath}>
                {displayName} ({count} project{count !== 1 ? 's' : ''})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Repository Selector */}
      {selectedNamespace && (
        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <GitMerge className="h-4 w-4" />
            Repository
          </label>
          <select
            value={selectedRepoUrl}
            onChange={(e) => onSelectedRepoUrlChange(e.target.value)}
            className="w-full p-2 border rounded-md bg-background"
            disabled={disabled}
          >
            <option value="">Choose a repository...</option>
            {filteredProjects.map((project) => (
              <option key={project.path_with_namespace} value={project.web_url}>
                {project.name} ({project.visibility || 'Unknown'})
              </option>
            ))}
          </select>
          {filteredProjects.length === 0 && (
            <p className="text-xs text-amber-600">
              No projects found in this namespace.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
